import { FieldPath, UseFormReturn } from "react-hook-form";

import { InfoTooltip } from "./InfoTooltip";
import { OptionalLabel } from "@/components/OptionalLabel";
import { OptionalTooltip } from "@/components/OptionalTooltip";
import { Row } from "@/components/Row";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { cn } from "@/lib/utils";

export const FormEnumSelect = <T extends Record<string, unknown>>({
  className,
  description,
  disabled,
  fieldName,
  form,
  infoTooltip,
  inputClassName,
  label,
  onChange,
  optional,
  options,
  placeholder,
}: {
  className?: string;
  description?: string;
  disabled?: boolean;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  infoTooltip?: string;
  inputClassName?: string;
  label: string;
  onChange?: (value: string) => void;
  optional?: boolean;
  // An option with a disabledReason can't be picked, and the reason shows on hover.
  options: { value: string; label: string; disabledReason?: string }[];
  placeholder?: string;
}) => {
  /* Render */
  const renderOption = (option: {
    value: string;
    label: string;
    disabledReason?: string;
  }) => {
    const item = (
      <SelectItem
        disabled={!!option.disabledReason}
        key={option.value}
        value={option.value}
      >
        {option.label}
      </SelectItem>
    );

    if (!option.disabledReason) return item;

    // Tooltip hangs off a wrapper, not the item: a disabled item is
    // pointer-events-none and would never fire a hover.
    return (
      <OptionalTooltip
        key={option.value}
        text={option.disabledReason}
        trackingLabel="Disabled select option tooltip"
      >
        <Row className="w-full">{item}</Row>
      </OptionalTooltip>
    );
  };

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        return (
          <FormItem className={cn("text-slate-700", className)}>
            <FormLabel className="inline-flex items-center gap-1">
              {label}
              {optional && <OptionalLabel className="ml-1.5" />}
              {infoTooltip && <InfoTooltip content={infoTooltip} />}
            </FormLabel>
            <Select
              disabled={disabled}
              onValueChange={(value) => {
                field.onChange(value);
                onChange?.(value);
              }}
              value={field.value as string}
            >
              <FormControl>
                <SelectTrigger className={inputClassName}>
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="text-slate-700">
                {options.map(renderOption)}
              </SelectContent>
            </Select>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
